import React, { useState } from "react";
import { useLocation } from "react-router-dom";

import { Impulse, useScoped } from "~/tools/impulse";
import { objectOf } from "~/tools/object-of";
import { useHandler } from "~/tools/use-handler";
import { usePermanent } from "~/tools/use-permanent";
import { useRequiredContext } from "~/tools/use-required-context";

import { ServiceTokenDrawer } from "../../../bottom_content/stackpacks/installation-guide/ServiceTokenDrawer";
import { ComponentLastChangeDrawer } from "../../../bottom_content/view/highlights-perspective/drawers/ComponentLastChangeDrawer";
import { YamlDisplayDrawer } from "../../../bottom_content/view/highlights-perspective/drawers/YamlDisplayDrawer";
import { MonitorStatusDrawer } from "../../../bottom_content/view/highlights-perspective/monitor-status-drawer/MonitorStatusDrawer";
import { RelatedResourcesDrawer } from "../../../bottom_content/view/highlights-perspective/related-resources-drawer";
import { ElementPropertiesLogsDrawer } from "../../../bottom_content/view/panes/east/element-details/element-details-drawer/ElementPropertiesLogsDrawer";
import { EventDiffDrawer } from "../../../bottom_content/view/panes/east/event-details/event-diff-drawer";
import { AiAssistantDrawer } from "../../../sessions/ai-assistant-drawer/ai-assistant-drawer";
import { useAiAssistantAvailability } from "../../../sessions/use-ai-assistant-availability";
import { AddToDashboardDrawer } from "../../_add-to-dashboard-drawer";
import { DashboardVariablesDrawer } from "../../_dashboard-variables-drawer";
import { ErrorDetailsDrawer, ViewFormDrawer } from "../../_drawer";
import { ElementJsonDefinitionDrawer, ElementPropertiesDrawer } from "../../_element-drawer";
import { EventPropertiesDrawer } from "../../_event-properties-drawer";
import { MonitorOutputDrawer } from "../../_monitor-output-drawer";
import { MonitorPreviewDrawer } from "../../_monitor-preview-drawer";
import { SetupDashboardDrawer } from "../../_setup-dashboard-drawer";
import { SetupDashboardVariableDrawer } from "../../_setup-dashboard-variable-drawer";
import { SetupDashboardWidgetDrawer } from "../../_setup-dashboard-widget-drawer/drawer";
import { MetricsDrawer } from "../../metrics-drawer";
import { BoundMetricsDrawer } from "../../metrics-drawer/bound";

import { ExplorationDrawerContextProvider } from "./_exploration-drawer-context";
import { ExplorationDrawerPreserveStateContextProvider } from "./_exploration-drawer-preserve-state-context";
import {
	ExplorationDrawerStateContext,
	type ExplorationDrawerStateContextValue,
} from "./_exploration-drawer-state-context";
import { ExplorationDrawerState, type ExplorationDrawerTable } from "./ExplorationDrawerState";

export const ExplorationDrawerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const location = useLocation();

	const context = usePermanent((): ExplorationDrawerStateContextValue => {
		const tables = Impulse<ReadonlyArray<ExplorationDrawerTable>>([]);

		return {
			drawers: tables,
			state: new ExplorationDrawerState((key, drawer) =>
				tables.setValue((current) => [...current, objectOf(drawer, key)]),
			),
		};
	});

	const [preservedState, setPreservedState] = useState(null);

	const preserveStateContextValue = React.useMemo(
		() => ({
			preservedState,
			setPreservedState,
		}),
		[preservedState],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies(location.pathname): close all drawers when the location changes
	React.useEffect(() => {
		context.drawers.setValue([]);
	}, [location.pathname, context.drawers]);

	return (
		<ExplorationDrawerStateContext.Provider value={context}>
			<ExplorationDrawerPreserveStateContextProvider value={preserveStateContextValue}>
				{children}
			</ExplorationDrawerPreserveStateContextProvider>
		</ExplorationDrawerStateContext.Provider>
	);
};

export const ExplorationDrawerContent: React.FC = () => {
	const { drawers } = useRequiredContext(ExplorationDrawerStateContext, {
		providerName: "ExplorationDrawerProvider",
	});

	return <ExplorationDrawerLevel drawers={drawers} index={0} />;
};

const ExplorationDrawerLevel: React.FC<{
	drawers: Impulse<ReadonlyArray<ExplorationDrawerTable>>;
	index: number;
}> = React.memo(({ drawers, index }) => {
	const enableAi = useAiAssistantAvailability();
	// there should be always one empty drawer element so the drawer animation works nice
	const table = useScoped((scope) => drawers.getValue(scope).at(index) ?? {}, [drawers, index]);
	const onClose = useHandler(() => {
		// the close handler keeps only the drawers before the current one
		// so that if a a drawer with nested drawers is closed, the nested drawers are also closed
		drawers.setValue((current) => current.slice(0, index));
	});

	return (
		<ExplorationDrawerContextProvider
			// there is not much reason to memoize the context value - the `children` will be a new object on every rerender
			// but the React.memo makes sure that the current component never re-renders due to static props (Impulse does not change value)
			value={{
				onClose,
				// inject the nested drawers into the current exploration drawer so they shifted accordingly
				children: <ExplorationDrawerLevel drawers={drawers} index={index + 1} />,
			}}
		>
			<ComponentLastChangeDrawer when={table.componentLastChange} />
			<YamlDisplayDrawer when={table.yamlDisplay} />
			<MonitorPreviewDrawer when={table.monitorPreview} />
			<ElementPropertiesLogsDrawer when={table.componentLogs} />
			<EventDiffDrawer when={table.eventDiff} />
			<MetricsDrawer when={table.metrics} />
			<BoundMetricsDrawer when={table.boundMetrics} />
			<MonitorStatusDrawer when={table.monitorStatus} />
			<RelatedResourcesDrawer when={table.relatedResources} />
			<MonitorOutputDrawer when={table.monitorOutput} />
			<ElementPropertiesDrawer when={table.elementProperties} />
			<ElementJsonDefinitionDrawer when={table.elementJsonDefinition} />
			<EventPropertiesDrawer when={table.eventProperties} />
			<ErrorDetailsDrawer when={table.errorDetails} />
			<ViewFormDrawer when={table.viewForm} />
			<SetupDashboardDrawer when={table.setupDashboard} />
			<AddToDashboardDrawer when={table.addToDashboard} />
			<SetupDashboardWidgetDrawer when={table.setupDashboardWidget} />
			<DashboardVariablesDrawer when={table.dashboardVariables} />
			<SetupDashboardVariableDrawer when={table.dashboardVariable} />
			<ServiceTokenDrawer when={table.serviceToken} />
			{enableAi && <AiAssistantDrawer when={table.aiAssistant} />}
		</ExplorationDrawerContextProvider>
	);
});
